'use client';

import { useMemo, useState, useCallback } from 'react';
import { useTranslations } from 'next-intl';
import { useQuery } from '@tanstack/react-query';
import { Printer, CheckCircle, Plus } from 'lucide-react';
import QRCode from 'react-qr-code';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { PrintPortal } from '@/components/print-portal';
import {
  PrintSection,
  PrintGrid,
  PrintField
} from '@/components/print-helpers';
import { registrationQueries } from '../services/registration.queries';
import { useAdvanceStageMutation } from '../services/registration.mutations';
import type { Registration } from '../types';
import type { DynamicFormField } from '@/features/dynamic-forms';

interface RegistrationSuccessDialogProps {
  containerNumber: string;
  registration?: Registration;
  registrationId?: number;
  position?: number;
  total?: number;
  onComplete?: () => void;
  onCreateNew?: () => void;
  onOpenChange?: (open: boolean) => void;
}

export function RegistrationSuccessDialog({
  containerNumber,
  registration,
  registrationId,
  position,
  total,
  onComplete,
  onCreateNew,
  onOpenChange
}: RegistrationSuccessDialogProps) {
  const t = useTranslations('registration');
  const advanceMutation = useAdvanceStageMutation();
  const completedStage = 'registration';
  const [printMode, setPrintMode] = useState(false);
  const [printQrMode, setPrintQrMode] = useState(false);

  const fetchId = registrationId ?? registration?.id ?? 0;
  const { data: fetchedData } = useQuery(registrationQueries.detail(fetchId));
  const reg =
    (fetchedData?.data as Registration | undefined) ?? registration ?? null;

  const tc = useTranslations('container');
  const na = tc('notAvailable');

  const dynamicForm = reg?.dynamic_form ?? null;
  const fields = useMemo<DynamicFormField[]>(
    () => dynamicForm?.fields ?? [],
    [dynamicForm]
  );

  const handlePrint = useCallback(() => {
    setPrintMode(true);
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        window.print();
        setPrintMode(false);
      });
    });
  }, []);

  const handlePrintQr = useCallback(() => {
    setPrintQrMode(true);
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        window.print();
        setPrintQrMode(false);
      });
    });
  }, []);

  const handleAdvanceAndCallback = useCallback(
    async (callback?: () => void) => {
      if (!callback) return;
      const id = registrationId ?? registration?.id;
      if (id) {
        try {
          await advanceMutation.mutateAsync({
            id,
            data: { from: completedStage }
          });
        } catch {
          return;
        }
      }
      callback();
    },
    [registrationId, registration?.id, advanceMutation, completedStage]
  );

  const hasSequence = typeof total === 'number' && total > 1;
  const isLastInSequence =
    !hasSequence || (typeof position === 'number' && position >= (total ?? 1));

  return (
    <>
      <Dialog open onOpenChange={onOpenChange}>
        <DialogContent className='max-h-[90dvh] overflow-y-auto sm:max-w-md'>
          <DialogHeader>
            {hasSequence && (
              <div className='mb-1 flex justify-center'>
                <span className='bg-muted text-muted-foreground rounded-full px-3 py-0.5 text-xs font-medium'>
                  {t('containerProgress', {
                    position: position ?? 1,
                    total: total ?? 1
                  })}
                </span>
              </div>
            )}
            <div className='mb-2 flex justify-center'>
              <CheckCircle className='h-10 w-10 text-green-500 sm:h-12 sm:w-12' />
            </div>
            <DialogTitle className='text-center'>
              {t('successTitle')}
            </DialogTitle>
            <DialogDescription className='text-center'>
              {t('successDescription')}
            </DialogDescription>
          </DialogHeader>

          <div className='bg-muted rounded-lg px-4 py-3 text-center sm:px-6 sm:py-4'>
            <p className='text-muted-foreground mb-1 text-xs sm:text-sm'>
              {t('registrationCode')}
            </p>
            <p className='text-foreground font-mono text-xl font-bold tracking-wider sm:text-2xl'>
              {containerNumber}
            </p>
          </div>

          <div className='flex justify-center py-1 sm:py-2'>
            <div className='rounded-lg bg-white p-2 sm:p-3'>
              <QRCode value={containerNumber} size={130} level='M' />
            </div>
          </div>

          {/* Actions — grouped by type */}
          <div className='border-border space-y-2 border-t pt-3'>
            {/* Print actions row */}
            <div className='flex gap-2'>
              <Button
                type='button'
                variant='outline'
                className='flex-1'
                onClick={handlePrint}
              >
                <Printer className='me-2 h-4 w-4 shrink-0' />
                {t('printFile')}
              </Button>
              {onComplete && (
                <Button
                  type='button'
                  variant='outline'
                  className='flex-1'
                  onClick={handlePrintQr}
                >
                  <Printer className='me-2 h-4 w-4 shrink-0' />
                  {t('printQrCode')}
                </Button>
              )}
            </div>

            {/* Workflow actions — stacked full-width */}
            {onComplete && (
              <Button
                type='button'
                className='w-full'
                onClick={() => handleAdvanceAndCallback(onComplete)}
                disabled={advanceMutation.isPending}
              >
                <CheckCircle className='me-2 h-4 w-4' />
                {t('completeRegistration')}
              </Button>
            )}
            {onCreateNew && isLastInSequence && (
              <Button
                type='button'
                variant='secondary'
                className='w-full'
                onClick={onCreateNew}
              >
                <Plus className='me-2 h-4 w-4' />
                {t('createNewContainer')}
              </Button>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* QR-only print */}
      {printQrMode && (
        <PrintPortal>
          <div data-print-zone className='hidden'>
            <div
              style={{
                fontFamily: 'Arial, sans-serif',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '10mm'
              }}
            >
              <QRCode value={containerNumber} size={180} level='M' />
              <p
                style={{
                  fontFamily: 'monospace',
                  fontSize: '16px',
                  fontWeight: 700,
                  marginTop: '12px',
                  letterSpacing: '0.1em'
                }}
              >
                {containerNumber}
              </p>
            </div>
          </div>
        </PrintPortal>
      )}

      {/* Full data print — matches container detail page layout */}
      {printMode && reg && (
        <PrintPortal>
          <div data-print-zone className='hidden'>
            <div
              style={{
                fontFamily: 'Arial, sans-serif',
                color: '#111',
                padding: '10mm'
              }}
            >
              {/* Print header */}
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                  borderBottom: '2px solid #222',
                  paddingBottom: '12px',
                  marginBottom: '20px'
                }}
              >
                <div>
                  <h1 style={{ fontSize: '20px', margin: 0, fontWeight: 700 }}>
                    {tc('containerDetails')}
                  </h1>
                  <p
                    style={{
                      fontSize: '13px',
                      color: '#555',
                      marginTop: '4px'
                    }}
                  >
                    {containerNumber}
                    {/* {stageLabels[reg.current_stage] ?? reg.current_stage} */}
                  </p>
                  <PrintField
                    label={tc('creationDate')}
                    value={(() => {
                      const d = new Date(reg.created_at);
                      const day = String(d.getDate()).padStart(2, '0');
                      const month = String(d.getMonth() + 1).padStart(2, '0');
                      const year = d.getFullYear();
                      return `${day}-${month}-${year}`;
                    })()}
                  />
                </div>
                <div style={{ textAlign: 'center' }}>
                  <QRCode value={containerNumber} size={80} level='M' />
                  <p
                    style={{
                      fontSize: '10px',
                      fontWeight: 600,
                      marginTop: '4px'
                    }}
                  >
                    {containerNumber}
                  </p>
                </div>
              </div>

              {/* Section: Registration Info */}
              <PrintSection title={tc('registrationInfo')}>
                <PrintGrid>
                  {reg.client && (
                    <PrintField
                      label={tc('clientName')}
                      value={reg.client.name}
                    />
                  )}
                  <PrintField
                    label={tc('usedTemplate')}
                    value={dynamicForm?.name || na}
                  />
                  <PrintField
                    label={tc('expectedPageCount')}
                    value={
                      reg.expected_page_count != null
                        ? String(reg.expected_page_count)
                        : na
                    }
                  />
                  <PrintField
                    label={tc('lastUpdate')}
                    value={new Date(reg.updated_at).toLocaleDateString()}
                  />
                </PrintGrid>
              </PrintSection>

              {/* Section: Dynamic form responses */}
              {fields.length > 0 &&
                reg.form_responses &&
                Object.keys(reg.form_responses).length > 0 && (
                  <PrintSection title={tc('formDetails')}>
                    <PrintGrid>
                      {fields.map((field) => {
                        const raw = reg.form_responses?.[field.id];
                        let display = na;
                        if (raw != null && raw !== '') {
                          if (field.type === 'yes_no') {
                            display = raw ? tc('statusComplete') : '—';
                          } else if (field.type === 'number') {
                            display = Number(raw).toLocaleString();
                          } else {
                            display = String(raw);
                          }
                        }
                        return (
                          <PrintField
                            key={field.id}
                            label={field.label}
                            value={display}
                          />
                        );
                      })}
                    </PrintGrid>
                  </PrintSection>
                )}

              {/* Footer */}
              <div
                style={{
                  marginTop: '24px',
                  paddingTop: '8px',
                  borderTop: '1px solid #ddd',
                  fontSize: '10px',
                  color: '#999',
                  textAlign: 'center'
                }}
              >
                {tc('printedOn', {
                  date: new Date().toLocaleString()
                })}
              </div>
            </div>
          </div>
        </PrintPortal>
      )}
    </>
  );
}
